import { useState, useCallback, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Upload, Trash2, Image, Check, Loader2, Play, FileText, Link, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { api } from '../../lib/api.ts';
import { cn } from './cn.ts';
import {
  type MediaAsset,
  type MediaListResponse,
  ALLOWED_IMAGE_TYPES,
  ALLOWED_VIDEO_TYPES,
  ALLOWED_TYPES,
  MAX_IMAGE_BYTES,
  MAX_VIDEO_BYTES,
  thumbUrl,
  formatBytes,
  getAssetIcon,
} from '../../lib/media.ts';

type TypeFilter = 'all' | 'image' | 'video' | 'document' | 'embed';

interface MediaPickerModalProps {
  /** Whether the modal is visible */
  open: boolean;
  /** Called when the user closes the modal without selecting */
  onClose: () => void;
  /** Called when the user selects (or uploads and selects) an asset */
  onSelect: (url: string, meta?: { srcset?: string; webpSrcset?: string; blurDataUrl?: string }) => void;
  /** Restrict which asset types are shown (default: all) */
  allowedTypes?: TypeFilter[];
}

// ─── MediaPickerModal ─────────────────────────────────────────────────────────

const PICKER_PAGE_SIZE = 24;

export function MediaPickerModal({ open, onClose, onSelect, allowedTypes }: MediaPickerModalProps) {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);
  const [tab, setTab] = useState<'library' | 'upload' | 'embed'>('library');
  const [embedUrl, setEmbedUrl] = useState('');
  const [embedPreview, setEmbedPreview] = useState<{ provider: string; title: string; thumbnailUrl: string | null } | null>(null);
  const [embedLoading, setEmbedLoading] = useState(false);
  const [embedError, setEmbedError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pickerPage, setPickerPage] = useState(1);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPickerPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSelectedId(null);
      setPendingUrl(null);
      setTab('library');
      setTypeFilter('all');
      setSearch('');
      setDebouncedSearch('');
      setPickerPage(1);
      setUploadError(null);
      setIsDragging(false);
      setEmbedUrl('');
      setEmbedPreview(null);
      setEmbedError(null);
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // ── Query: list assets (server-side) ───────────────────────────────────────
  const visibleTypes = allowedTypes ?? ['all', 'image', 'video', 'document', 'embed'];
  const queryParams = new URLSearchParams();
  queryParams.set('limit', String(PICKER_PAGE_SIZE));
  queryParams.set('offset', String((pickerPage - 1) * PICKER_PAGE_SIZE));
  if (debouncedSearch) queryParams.set('q', debouncedSearch);
  if (typeFilter !== 'all') {
    queryParams.set('type', typeFilter);
  } else if (allowedTypes && !allowedTypes.includes('all')) {
    // When showing "All" but restricted to certain types, use multi-type filter
    queryParams.set('types', allowedTypes.filter(t => t !== 'all').join(','));
  }

  const { data, isLoading, isError } = useQuery<MediaListResponse>({
    queryKey: ['media-picker', { page: pickerPage, q: debouncedSearch, type: typeFilter, allowedTypes }],
    queryFn: () => api.get(`/api/v1/media?${queryParams.toString()}`).then((r) => r.data),
    enabled: open,
    staleTime: 10_000,
  });

  const assets = data?.items ?? [];
  const totalItems = (data as any)?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / PICKER_PAGE_SIZE));

  // ── Mutation: delete ─────────────────────────────────────────────────────────
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/media/${id}`),
    onSuccess: (_data, id) => {
      if (selectedId === id) setSelectedId(null);
      qc.invalidateQueries({ queryKey: ['media-assets'] });
    },
  });

  // ── Upload flow ──────────────────────────────────────────────────────────────
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      setUploadError(null);

      // 1. Request presigned URL + asset ID
      const { data: urlData } = await api.post<{
        uploadUrl: string;
        publicUrl: string;
        assetId: string;
        assetType: string;
      }>('/api/v1/media/upload-url', {
        fileName: file.name,
        contentType: file.type,
        sizeBytes: file.size,
      });

      // 2. PUT directly to S3
      const res = await fetch(urlData.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!res.ok) throw new Error('S3 upload failed');

      // 3. Confirm metadata in DB
      try {
        await api.post(`/api/v1/media/${urlData.assetId}/confirm`, { sizeBytes: file.size });
      } catch (confirmErr) {
        throw new Error(
          'File uploaded to S3 but could not save metadata. ' +
            'Please refresh and try again, or contact support.',
        );
      }

      return urlData;
    },
    onSuccess: (urlData) => {
      // Store the public URL immediately — used by handleConfirmSelect while
      // the query cache is still refetching (avoids silent no-op race).
      setPendingUrl(urlData.publicUrl);
      setSelectedId(urlData.assetId);
      qc.invalidateQueries({ queryKey: ['media-assets'] });
      qc.invalidateQueries({ queryKey: ['media-picker'] });
      setTab('library');
      // Reset the file input so the same file can be uploaded again if needed.
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Upload failed. Please try again.';
      setUploadError(msg);
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
  });

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      const file = files[0];

      if (!ALLOWED_TYPES.includes(file.type)) {
        setUploadError(
          `File type '${file.type}' is not supported. ` +
            'Use JPEG, PNG, GIF, WebP, SVG, TIFF, BMP, HEIC/HEIF, or MP4.',
        );
        return;
      }
      const maxBytes = ALLOWED_VIDEO_TYPES.includes(file.type) ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES;
      if (file.size > maxBytes) {
        setUploadError(
          `File too large (${formatBytes(file.size)}). Maximum size is ${formatBytes(maxBytes)}.`,
        );
        return;
      }

      uploadMutation.mutate(file);
    },
    [uploadMutation],
  );

  const handleConfirmSelect = () => {
    if (!selectedId) return;

    const asset = assets.find((a) => a.id === selectedId);
    // For embeds use embedUrl (the original YouTube/Vimeo URL), for others use publicUrl
    const url = asset?.assetType === 'embed'
      ? (asset.embedUrl ?? asset.publicUrl ?? pendingUrl)
      : (asset?.publicUrl ?? pendingUrl);

    if (url) {
      // Build srcset + webpSrcset from thumbnails if available
      let srcset: string | undefined;
      let webpSrcset: string | undefined;
      let blurDataUrl: string | undefined;
      if (asset?.thumbnails && asset.assetType === 'image') {
        const t = asset.thumbnails as Record<string, string>;
        const jpegParts: string[] = [];
        const webpParts: string[] = [];
        if (t.w300) jpegParts.push(`${t.w300} 300w`);
        if (t.w600) jpegParts.push(`${t.w600} 600w`);
        if (t.w1200) jpegParts.push(`${t.w1200} 1200w`);
        if (asset.publicUrl && jpegParts.length > 0) jpegParts.push(`${asset.publicUrl} 1920w`);
        if (t.w300webp) webpParts.push(`${t.w300webp} 300w`);
        if (t.w600webp) webpParts.push(`${t.w600webp} 600w`);
        if (t.w1200webp) webpParts.push(`${t.w1200webp} 1200w`);
        if (jpegParts.length > 0) srcset = jpegParts.join(', ');
        if (webpParts.length > 0) webpSrcset = webpParts.join(', ');
      }
      if (asset?.blurDataUrl) blurDataUrl = asset.blurDataUrl;
      const meta = (srcset || webpSrcset || blurDataUrl) ? { srcset, webpSrcset, blurDataUrl } : undefined;
      onSelect(url, meta);
      onClose();
    }
  };

  if (!open) return null;

  return (
    // Backdrop — clicking outside the modal panel closes it
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="media-picker-title"
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      {/* Modal panel — stop propagation so clicks inside don't close the dialog */}
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <h2 id="media-picker-title" className="text-lg font-semibold text-zinc-900">
            Media Library
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-zinc-100 text-zinc-500 hover:text-zinc-700 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-6 pt-3 border-b border-zinc-100">
          {([
            { key: 'library' as const, label: 'Library' },
            { key: 'upload' as const, label: 'Upload File' },
            { key: 'embed' as const, label: 'Embed URL' },
          ]).map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={cn(
                'px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors',
                tab === key
                  ? 'border-zinc-800 text-zinc-900'
                  : 'border-transparent text-zinc-500 hover:text-zinc-700',
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
          {/* ── Library tab ── */}
          {tab === 'library' && (
            <>
              {/* Search + type filter */}
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <div className="relative flex-1 min-w-[180px]">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search files…"
                    className="w-full h-8 pl-9 pr-8 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                  />
                  {search && (
                    <button onClick={() => { setSearch(''); setPickerPage(1); }} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-300 hover:text-zinc-500">
                      <X size={13} />
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-1 bg-zinc-100 rounded-lg p-0.5">
                  {(
                    [
                      { value: 'all', label: 'All' },
                      { value: 'image', label: 'Images' },
                      { value: 'video', label: 'Video' },
                      { value: 'document', label: 'Docs' },
                      { value: 'embed', label: 'Embed' },
                    ] as { value: TypeFilter; label: string }[]
                  ).filter(({ value }) => visibleTypes.includes(value) || value === 'all').map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => { setTypeFilter(value); setPickerPage(1); }}
                      className={cn(
                        'px-2.5 py-1 text-xs font-medium rounded-md transition-colors',
                        typeFilter === value
                          ? 'bg-white text-zinc-800 shadow-sm'
                          : 'text-zinc-500 hover:text-zinc-700',
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {isLoading && (
                <div className="flex items-center justify-center py-16 text-zinc-400">
                  <Loader2 size={24} className="animate-spin mr-2" />
                  Loading…
                </div>
              )}
              {isError && (
                <div className="text-center py-16 text-red-500 text-sm">
                  Failed to load media assets.
                </div>
              )}
              {!isLoading && !isError && totalItems === 0 && !debouncedSearch && typeFilter === 'all' && (
                <div className="flex flex-col items-center justify-center py-16 text-zinc-400 gap-3">
                  <Image size={40} className="opacity-40" />
                  <p className="text-sm">No assets yet. Upload your first file.</p>
                  <button
                    onClick={() => setTab('upload')}
                    className="text-sm font-medium text-zinc-700 underline underline-offset-2 hover:text-zinc-900"
                  >
                    Go to Upload
                  </button>
                </div>
              )}
              {!isLoading && !isError && totalItems === 0 && (debouncedSearch || typeFilter !== 'all') && (
                <div className="flex flex-col items-center justify-center py-16 text-zinc-400 gap-3">
                  <Image size={40} className="opacity-40" />
                  <p className="text-sm">No files match your search.</p>
                </div>
              )}
              {!isLoading && !isError && assets.length > 0 && (
                <>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                    {assets.map((asset) => (
                      <AssetTile
                        key={asset.id}
                        asset={asset}
                        isSelected={selectedId === asset.id}
                        onSelect={() => setSelectedId(asset.id === selectedId ? null : asset.id)}
                        onDelete={() => {
                          if (confirm(`Delete "${asset.fileName}"?`)) {
                            deleteMutation.mutate(asset.id);
                          }
                        }}
                      />
                    ))}
                  </div>
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-4">
                      <button
                        onClick={() => setPickerPage(Math.max(1, pickerPage - 1))}
                        disabled={pickerPage <= 1}
                        className="p-1.5 rounded-lg hover:bg-zinc-100 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft size={16} />
                      </button>
                      <span className="text-xs text-zinc-500">
                        Page {pickerPage} of {totalPages}
                      </span>
                      <button
                        onClick={() => setPickerPage(Math.min(totalPages, pickerPage + 1))}
                        disabled={pickerPage >= totalPages}
                        className="p-1.5 rounded-lg hover:bg-zinc-100 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* ── Upload tab ── */}
          {tab === 'upload' && (
            <div className="flex flex-col items-center justify-center py-8 gap-4">
              {/* Drop zone */}
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragging(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  handleFiles(e.dataTransfer.files);
                }}
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                  'w-full max-w-md border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-colors',
                  isDragging
                    ? 'border-zinc-800 bg-zinc-50'
                    : 'border-zinc-300 hover:border-zinc-500 hover:bg-zinc-50',
                )}
              >
                {uploadMutation.isPending ? (
                  <div className="flex flex-col items-center gap-3 text-zinc-500">
                    <Loader2 size={32} className="animate-spin" />
                    <p className="text-sm font-medium">Uploading…</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3 text-zinc-400">
                    <Upload size={32} />
                    <div>
                      <p className="text-sm font-medium text-zinc-700">Drop a file here</p>
                      <p className="text-xs text-zinc-400 mt-1">or click to browse</p>
                    </div>
                    <p className="text-xs text-zinc-400">
                      Images (JPEG, PNG, GIF, WebP, SVG, TIFF, BMP, HEIC/HEIF) — max 20 MB
                    </p>
                    <p className="text-xs text-zinc-400">Video (MP4) — max 200 MB</p>
                  </div>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept={ALLOWED_TYPES.join(',')}
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
              />

              {uploadError && (
                <p className="text-sm text-red-600 max-w-md text-center">{uploadError}</p>
              )}
            </div>
          )}

          {/* ── Embed tab ── */}
          {tab === 'embed' && (
            <div className="flex flex-col items-center py-8 gap-4 max-w-md mx-auto w-full">
              <p className="text-sm text-zinc-600 text-center">
                Paste a YouTube, Vimeo, or other supported URL
              </p>
              <input
                type="url"
                value={embedUrl}
                onChange={(e) => {
                  setEmbedUrl(e.target.value);
                  setEmbedPreview(null);
                  setEmbedError(null);
                }}
                placeholder="https://www.youtube.com/watch?v=..."
                className="w-full h-10 px-3 bg-white border border-zinc-300 rounded-lg text-sm text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
              />
              {!embedPreview && !embedLoading && embedUrl.trim().length > 10 && (
                <button
                  onClick={async () => {
                    setEmbedLoading(true);
                    setEmbedError(null);
                    try {
                      const res = await api.post('/api/v1/media/embed/preview', { url: embedUrl.trim() });
                      setEmbedPreview(res.data as any);
                    } catch {
                      setEmbedError('Could not load embed preview. Check the URL.');
                    } finally {
                      setEmbedLoading(false);
                    }
                  }}
                  className="px-4 py-2 bg-zinc-900 text-white text-sm font-medium rounded-lg hover:bg-zinc-700 transition-colors"
                >
                  Preview
                </button>
              )}
              {embedLoading && (
                <div className="flex items-center gap-2 text-zinc-400 text-sm">
                  <Loader2 size={16} className="animate-spin" /> Loading preview…
                </div>
              )}
              {embedError && <p className="text-sm text-red-500">{embedError}</p>}
              {embedPreview && (
                <div className="w-full border border-zinc-200 rounded-xl overflow-hidden bg-white">
                  {embedPreview.thumbnailUrl && (
                    <img src={embedPreview.thumbnailUrl} alt="" className="w-full h-40 object-cover" />
                  )}
                  <div className="p-3">
                    <p className="text-sm font-medium text-zinc-800 truncate">{embedPreview.title}</p>
                    <p className="text-xs text-zinc-500 uppercase tracking-wide mt-0.5">{embedPreview.provider}</p>
                  </div>
                  <div className="px-3 pb-3">
                    <button
                      onClick={async () => {
                        setEmbedLoading(true);
                        try {
                          await api.post('/api/v1/media/embed', { url: embedUrl.trim() });
                          qc.invalidateQueries({ queryKey: ['media-picker'] });
                          // Use the embed URL directly
                          onSelect(embedUrl.trim());
                          onClose();
                        } catch {
                          setEmbedError('Failed to save embed.');
                        } finally {
                          setEmbedLoading(false);
                        }
                      }}
                      disabled={embedLoading}
                      className="w-full py-2 bg-zinc-900 text-white text-sm font-medium rounded-lg hover:bg-zinc-700 disabled:opacity-50 transition-colors"
                    >
                      Save & Use This Embed
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-zinc-200 bg-zinc-50 rounded-b-2xl">
          <p className="text-xs text-zinc-500">
            {selectedId
              ? `1 asset selected`
              : totalItems > 0
                ? `${totalItems} asset${totalItems !== 1 ? 's' : ''} in library`
                : ''}
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-zinc-700 bg-white border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmSelect}
              disabled={!selectedId}
              className="px-4 py-2 text-sm font-medium text-white bg-zinc-900 rounded-lg hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Use Selected
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── AssetTile ────────────────────────────────────────────────────────────────

function AssetTile({
  asset,
  isSelected,
  onSelect,
  onDelete,
}: {
  asset: MediaAsset;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
}) {
  const thumb = thumbUrl(asset);
  const isDocument = asset.assetType === 'document';
  const isEmbed = asset.assetType === 'embed';
  const icon = getAssetIcon(asset);

  return (
    <div
      onClick={onSelect}
      className={cn(
        'relative group rounded-xl border-2 overflow-hidden cursor-pointer transition-all',
        isSelected ? 'border-zinc-800 shadow-md' : 'border-zinc-200 hover:border-zinc-400',
      )}
    >
      {/* Thumbnail */}
      <div className="aspect-square bg-zinc-50 flex items-center justify-center overflow-hidden">
        {thumb ? (
          <img
            src={thumb}
            alt={asset.fileName}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : isDocument ? (
          <div className="flex flex-col items-center justify-center gap-1 text-zinc-400">
            <FileText size={28} />
            <span className="text-[10px] uppercase font-semibold tracking-wide">{icon}</span>
          </div>
        ) : isEmbed ? (
          <div className="flex flex-col items-center justify-center gap-1 text-zinc-400">
            <Link size={28} />
            <span className="text-[10px] uppercase font-semibold tracking-wide">
              {asset.embedProvider ?? 'embed'}
            </span>
          </div>
        ) : (
          <img
            src={thumb ?? undefined}
            alt={asset.fileName}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        )}
      </div>

      {/* Video play badge */}
      {asset.assetType === 'video' && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-8 h-8 rounded-full bg-black/60 flex items-center justify-center">
            <Play size={14} className="text-white ml-0.5" fill="white" />
          </div>
        </div>
      )}

      {/* Embed play badge (when thumbnail is shown) */}
      {isEmbed && thumb && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-8 h-8 rounded-full bg-black/60 flex items-center justify-center">
            <Play size={14} className="text-white ml-0.5" fill="white" />
          </div>
        </div>
      )}

      {/* Thumbnail processing spinner — shown while Lambda generates thumbnails */}
      {asset.thumbnailStatus === 'pending' && !isSelected && (
        <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-white/90 flex items-center justify-center shadow">
          <Loader2 size={11} className="animate-spin text-zinc-500" />
        </div>
      )}

      {/* Selected indicator */}
      {isSelected && (
        <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-zinc-800 flex items-center justify-center shadow">
          <Check size={11} className="text-white" />
        </div>
      )}

      {/* Delete button (shown on hover) */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="absolute top-1.5 left-1.5 w-6 h-6 rounded-full bg-red-500/90 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
        title="Delete asset"
      >
        <Trash2 size={11} />
      </button>

      {/* Name tooltip (bottom) */}
      <div className="absolute bottom-0 inset-x-0 bg-black/60 px-1.5 py-1 text-white text-[10px] truncate opacity-0 group-hover:opacity-100 transition-opacity">
        {asset.fileName}
      </div>
    </div>
  );
}
